// src/components/admin/ClientsManagement.js
import React, { useState, useEffect } from "react";
import { ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Search,
  User,
  FileText,
  ShoppingCart,
  Clipboard,
  Image,
} from "lucide-react";

const ClientDashboardPlaceholder = ({
  setClients,
  setFilteredClients,
  toggleAdvancedSearch,
  showAdvancedSearch,
  setShowAdvancedSearch,
  advancedFilters,
  setAdvancedFilters,
  handleApplyFilters,
}) => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    recentOrders: [],
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showNewClientModal, setShowNewClientModal] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        const clientsRes = await fetch("/api/admin/clients", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const clientsData = await clientsRes.json();
        console.log("Fetched clients:", clientsData);

        // Update clients data in parent component
        if (Array.isArray(clientsData)) {
          setClients(clientsData);
          setFilteredClients(clientsData);
        }

        // Calculate metrics
        const totalClients = Array.isArray(clientsData)
          ? clientsData.length
          : 0;
        const activeClients = Array.isArray(clientsData)
          ? clientsData.filter(
              (c) =>
                c.lastLogin &&
                new Date(c.lastLogin) >
                  new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            ).length
          : 0;

        // Fetch recent orders
        const ordersRes = await fetch("/api/orders?limit=5", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ordersData = await ordersRes.json();
        const recentOrders = ordersData.orders?.slice(0, 5) || [];

        setStats({
          totalClients,
          activeClients,
          recentOrders,
        });
      } catch (error) {
        console.error("Error fetching client stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [setClients, setFilteredClients]);

  const handleCreateClient = () => {
    setShowNewClientModal(true);
  };

  const closeNewClientModal = () => {
    setShowNewClientModal(false);
  };

  const handleNewClientSuccess = (newClient) => {
    // Update clients list with the new client
    if (newClient && setClients && typeof setClients === "function") {
      setClients((prevClients) => {
        if (!prevClients) return [newClient];
        return [newClient, ...prevClients];
      });
    }

    if (
      newClient &&
      setFilteredClients &&
      typeof setFilteredClients === "function"
    ) {
      setFilteredClients((prevClients) => {
        if (!prevClients) return [newClient];
        return [newClient, ...prevClients];
      });
    }

    // Close the modal after a delay
    setTimeout(() => {
      setShowNewClientModal(false);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Dashboard Header */}
      <div className="flex justify-between items-top mb-6">
        <h2 className="text-xl font-semibold">Client Dashboard</h2>
        <button
          onClick={handleCreateClient}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm lg:text-base flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          New Client
        </button>
      </div>

      {/* Stats Cards - 3 cards in a row on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow flex items-center">
          <div className="rounded-full p-3 bg-blue-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-gray-500 text-sm">Total Clients</p>
            <h3 className="font-bold text-xl">{stats.totalClients}</h3>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow flex items-center">
          <div className="rounded-full p-3 bg-green-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-gray-500 text-sm">Active Clients (30d)</p>
            <h3 className="font-bold text-xl">{stats.activeClients}</h3>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow flex items-center">
          <div className="rounded-full p-3 bg-purple-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-purple-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <div className="ml-4">
            <p className="text-gray-500 text-sm">Activity Rate</p>
            <h3 className="font-bold text-xl">
              {stats.totalClients
                ? `${Math.round(
                    (stats.activeClients / stats.totalClients) * 100
                  )}%`
                : "0%"}
            </h3>
          </div>
        </div>
      </div>

      {/* Quick Actions - 4 items in a row on desktop, 2x2 grid on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-4 mb-6">
        <button
          onClick={toggleAdvancedSearch}
          className="bg-white p-4 rounded-lg shadow flex flex-col items-center hover:shadow-lg transition-shadow"
        >
          <div className="rounded-full p-3 bg-indigo-100 mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-indigo-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <span className="text-sm font-medium text-center">
            Advanced Search
          </span>
        </button>

        <button
          onClick={() => navigate("/admin/uploads")}
          className="bg-white p-4 rounded-lg shadow flex flex-col items-center hover:shadow-lg transition-shadow"
        >
          <div className="rounded-full p-3 bg-green-100 mb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <span className="text-sm font-medium text-center">
            Client Uploads
          </span>
        </button>

        {/* <button 
          onClick={() => navigate('/admin/sales')}
          className="bg-white p-4 rounded-lg shadow flex flex-col items-center hover:shadow-lg transition-shadow"
        >
          <div className="rounded-full p-3 bg-blue-100 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-center">Sales Report</span>
        </button> */}

        {/* <button 
          onClick={() => navigate('/admin/security')}
          className="bg-white p-4 rounded-lg shadow flex flex-col items-center hover:shadow-lg transition-shadow"
        >
          <div className="rounded-full p-3 bg-red-100 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <span className="text-sm font-medium text-center">Security</span>
        </button> */}
      </div>
      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeNewClientModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              <X size={24} />
            </button>
            <div className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Add New Client</h2>
              <NewClientForm
                onClose={closeNewClientModal}
                onSuccess={handleNewClientSuccess}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NewClientForm = ({ onClose, onSuccess }) => {
  const [forlgata, setForlgata] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForlgata({
      ...forlgata,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      console.log("Submitting form data:", {
        ...forlgata,
        password: "[REDACTED]",
      });

      const response = await fetch("/api/admin/clients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(forlgata),
      });

      const data = await response.json();

      console.log("API response:", {
        status: response.status,
        ok: response.ok,
        data: data,
      });

      if (response.ok) {
        setSuccess(true);
        // Show success message for 1.5 seconds then close
        setTimeout(() => {
          if (onSuccess && typeof onSuccess === "function") {
            onSuccess(data.user);
          }
          onClose();
        }, 1500);
      } else {
        setError(data.error || "Failed to create client");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      setError(
        "An unexpected error occurred. The client may have been created but we couldn't confirm."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Client created successfully!
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            First Name*
          </label>
          <input
            type="text"
            name="firstName"
            value={forlgata.firstName}
            onChange={handleChange}
            required
            className="w-full p-2 border rounded"
            disabled={loading || success}
          />
        </div>

        <div>
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Last Name*
          </label>
          <input
            type="text"
            name="lastName"
            value={forlgata.lastName}
            onChange={handleChange}
            required
            className="w-full p-2 border rounded"
            disabled={loading || success}
          />
        </div>
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Email*
        </label>
        <input
          type="email"
          name="email"
          value={forlgata.email}
          onChange={handleChange}
          required
          className="w-full p-2 border rounded"
          disabled={loading || success}
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Phone
        </label>
        <input
          type="tel"
          name="phone"
          value={forlgata.phone}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          disabled={loading || success}
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Company
        </label>
        <input
          type="text"
          name="company"
          value={forlgata.company}
          onChange={handleChange}
          className="w-full p-2 border rounded"
          disabled={loading || success}
        />
      </div>

      <div>
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Initial Password*
        </label>
        <input
          type="password"
          name="password"
          value={forlgata.password}
          onChange={handleChange}
          required
          className="w-full p-2 border rounded"
          disabled={loading || success}
        />
        <p className="text-xs text-gray-500 mt-1">
          The user will be prompted to change this on first login
        </p>
      </div>

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
          disabled={loading || success}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || success}
          className={`px-4 py-2 ${
            success ? "bg-green-500" : "bg-blue-500 hover:bg-blue-600"
          } text-white rounded flex items-center`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating...
            </>
          ) : success ? (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Created!
            </>
          ) : (
            "Create Client"
          )}
        </button>
      </div>
    </form>
  );
};

const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [notification, setNotification] = useState("");
  const [newNote, setNewNote] = useState("");
  const navigate = useNavigate();
  const [showClientList, setShowClientList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [adminModal, setAdminModal] = useState({
    show: false,
    client: null,
    isGranting: false,
  });

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    client: null,
  });

  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    dateFrom: "",
    dateTo: "",
    hasOrders: false,
    status: "all",
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);

  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleToggleAdminClick = (client) => {
    const isGranting = !client.isAdmin;
    setAdminModal({
      show: true,
      client: client,
      isGranting: isGranting,
    });
  };

  // New function to actually change the admin status after confirmation
  const confirmToggleAdmin = async () => {
    try {
      const token = localStorage.getItem("token");
      const client = adminModal.client;
      const newStatus = !client.isAdmin;

      const res = await fetch(`/api/admin/clients/${client._id}/admin`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isAdmin: newStatus }),
      });

      const data = await res.json();
      if (res.ok) {
        setNotification("Client admin status updated successfully.");
        setClients((prev) =>
          prev.map((c) =>
            c._id === client._id ? { ...c, isAdmin: newStatus } : c
          )
        );
        if (selectedClient && selectedClient._id === client._id) {
          setSelectedClient({ ...selectedClient, isAdmin: newStatus });
          setClientDetails(
            (prev) =>
              prev && {
                ...prev,
                client: { ...prev.client, isAdmin: newStatus },
              }
          );
        }
      } else {
        setNotification(data.error || "Failed to update admin status.");
      }
    } catch (error) {
      console.error(error);
      setNotification("Error updating admin status.");
    } finally {
      setAdminModal({ show: false, client: null, isGranting: false });
    }
  };

  const handleDownloadImage = async (imageUrl, fileName) => {
    try {
      if (!imageUrl) {
        alert("No image URL available");
        return;
      }

      // Handle different URL formats
      let fixedUrl = imageUrl;

      if (imageUrl.startsWith("/upload/")) {
        fixedUrl = `/api${imageUrl}`;
      } else if (imageUrl.startsWith("data:")) {
        // Handle base64 images
        const link = document.createElement("a");
        link.href = imageUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      } else if (!imageUrl.startsWith("http") && !imageUrl.startsWith("/api")) {
        // Assume it's a relative path
        fixedUrl = `/api/upload/orders/${imageUrl}`;
      }

      const response = await fetch(fixedUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download the image. Please try again.");
    }
  };

  useEffect(() => {
    if (clients.length > 0) {
      const filtered = clients.filter((client) =>
        `${client.firstName || ""} ${client.lastName || ""} ${
          client.email || ""
        }`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [clients, searchQuery]);

  // Fetch clients list on mount
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch("/api/admin/clients", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        console.log("DEBUG: Clients fetched:", data);
        setClients(data);
      } catch (error) {
        console.error("Error fetching clients", error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  // When a client is selected, fetch detailed info and files in parallel
  useEffect(() => {
    if (!selectedClient) {
      setClientDetails(null);
      return;
    }
    const fetchClientDetails = async () => {
      setDetailsLoading(true);
      try {
        const token = localStorage.getItem("token");
        const [detailsRes, filesRes] = await Promise.all([
          fetch(`/api/admin/clients/${selectedClient._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`/api/admin/clients/${selectedClient._id}/files`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const detailsData = await detailsRes.json();
        const filesData = await filesRes.json();
        console.log("DEBUG: Client details fetched:", detailsData);
        console.log("DEBUG: Files fetched:", filesData);
        setClientDetails({
          client: detailsData.client,
          orders: detailsData.orders || [],
          contacts: detailsData.contacts || [],
          invoices: detailsData.invoices || [],
          notes: detailsData.notes || [],
          files: filesData || [],
        });
      } catch (error) {
        console.error("Error fetching client details", error);
      } finally {
        setDetailsLoading(false);
      }
    };
    fetchClientDetails();
  }, [selectedClient]);

  // First define filteredClients
  // const filteredClients = clients.filter((client) =>
  //   `${client.firstName} ${client.lastName} ${client.email}`
  //     .toLowerCase()
  //     .includes(searchQuery.toLowerCase())
  // );

  // Then define sortedClients
  const sortedClients = [...filteredClients].sort((a, b) => {
    // If you have a createdAt field, use this:
    // return new Date(b.createdAt) - new Date(a.createdAt);

    // If you don't have a date field, sort by ID (assuming newer clients have larger IDs)
    // This is just a fallback method - adjust according to your data structure
    return b._id > a._id ? 1 : -1;
  });

  // Now use sortedClients for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedClients.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(sortedClients.length / usersPerPage);

  // Define the Pagination component after having all the required variables
  const Pagination = () => {
    return (
      <div className="flex justify-center items-center mt-4 space-x-2">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded ${
            currentPage === 1
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Prev
        </button>

        <span className="px-3 py-1">
          Page {currentPage} of {totalPages || 1}
        </span>

        <button
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages || totalPages === 0}
          className={`px-3 py-1 rounded ${
            currentPage === totalPages || totalPages === 0
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  const handleToggleAdmin = async (client) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = !client.isAdmin;
      const res = await fetch(`/api/admin/clients/${client._id}/admin`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isAdmin: newStatus }),
      });
      const data = await res.json();
      if (res.ok) {
        setNotification("Client admin status updated successfully.");
        setClients((prev) =>
          prev.map((c) =>
            c._id === client._id ? { ...c, isAdmin: newStatus } : c
          )
        );
        if (selectedClient && selectedClient._id === client._id) {
          setSelectedClient({ ...selectedClient, isAdmin: newStatus });
          setClientDetails(
            (prev) =>
              prev && {
                ...prev,
                client: { ...prev.client, isAdmin: newStatus },
              }
          );
        }
      } else {
        setNotification(data.error || "Failed to update admin status.");
      }
    } catch (error) {
      console.error(error);
      setNotification("Error updating admin status.");
    }
  };

  const handleResetPassword = async (client) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `/api/admin/clients/${client._id}/reset-password`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      if (res.ok) {
        setNotification("Reset password request sent successfully.");
      } else {
        setNotification(data.error || "Failed to send reset password request.");
      }
    } catch (error) {
      console.error(error);
      setNotification("Error sending reset password request.");
    }
  };

  const handleDeleteUser = async (client) => {
    // Show the delete confirmation modal instead of window.confirm
    setDeleteModal({
      show: true,
      client: client,
    });
  };

  // 4. Add this new function to handle the actual deletion after confirmation
  const confirlgeleteUser = async () => {
    const client = deleteModal.client;
    if (!client) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/admin/clients/${client._id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        // Close the modal
        setDeleteModal({ show: false, client: null });

        // Show success animation
        setDeleteSuccess(true);

        // Hide success animation after 2 seconds
        setTimeout(() => {
          setDeleteSuccess(false);
        }, 2000);

        setNotification("User account deleted successfully");

        // Remove the user from the clients list
        setClients((prevClients) => {
          if (!prevClients) return [];
          return prevClients.filter((c) => c._id !== client._id);
        });

        setFilteredClients((prevClients) => {
          if (!prevClients) return [];
          return prevClients.filter((c) => c._id !== client._id);
        });

        // Clear selected client if it was the deleted one
        if (selectedClient && selectedClient._id === client._id) {
          setSelectedClient(null);
          setClientDetails(null);
          setShowClientList(true); // Show the client list after deletion
        }
      } else {
        setDeleteModal({ show: false, client: null });
        setNotification(data.error || "Failed to delete user account");
        console.error("Delete user error:", data);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setDeleteModal({ show: false, client: null });
      setNotification("Error deleting user account");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setNotification("Note content cannot be empty.");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `/api/admin/clients/${selectedClient._id}/notes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ note: newNote }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setNotification("Note added successfully.");
        setClientDetails((prev) => ({
          ...prev,
          notes: [data.note, ...prev.notes],
        }));
        setNewNote("");
      } else {
        setNotification(data.error || "Failed to add note.");
      }
    } catch (error) {
      console.error("Error adding note:", error);
      setNotification("Error adding note.");
    }
  };

  const handleApplyFilters = async (e) => {
    if (e) e.preventDefault();

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Construct query parameters based on filters
      const queryParams = new URLSearchParams();

      if (advancedFilters.dateFrom) {
        queryParams.append("startDate", advancedFilters.dateFrom);
      }

      if (advancedFilters.dateTo) {
        queryParams.append("endDate", advancedFilters.dateTo);
      }

      if (advancedFilters.status !== "all") {
        queryParams.append("status", advancedFilters.status);
      }

      if (advancedFilters.hasOrders) {
        queryParams.append("hasOrders", "true");
      }

      // Make API request with filters
      const res = await fetch(`/api/admin/clients?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      // Update clients list with filtered results
      setClients(data);
      setFilteredClients(data);
      setCurrentPage(1);
      setShowAdvancedSearch(false);
    } catch (error) {
      console.error("Error applying filters:", error);
      setNotification("Error applying filters");
    } finally {
      setLoading(false);
    }
  };

  // Also move the renderAdvancedSearch function outside of ClientDashboardPlaceholder
  const renderAdvancedSearch = () => (
    <div className="bg-white p-4 rounded-lg shadow-lg mx-2 my-4 z-10 absolute top-16 right-0 left-0 lg:left-auto lg:w-96">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-lg">Advanced Client Search</h3>
        <button
          onClick={() => setShowAdvancedSearch(false)}
          className="text-gray-500 hover:text-gray-800"
        >
          <X size={20} />
        </button>
      </div>
      <form onSubmit={handleApplyFilters}>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Registration Date From
            </label>
            <input
              type="date"
              className="w-full p-2 border rounded"
              value={advancedFilters.dateFrom}
              onChange={(e) =>
                setAdvancedFilters({
                  ...advancedFilters,
                  dateFrom: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Registration Date To
            </label>
            <input
              type="date"
              className="w-full p-2 border rounded"
              value={advancedFilters.dateTo}
              onChange={(e) =>
                setAdvancedFilters({
                  ...advancedFilters,
                  dateTo: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Client Status
            </label>
            <select
              className="w-full p-2 border rounded"
              value={advancedFilters.status}
              onChange={(e) =>
                setAdvancedFilters({
                  ...advancedFilters,
                  status: e.target.value,
                })
              }
            >
              <option value="all">All Clients</option>
              <option value="active">Active (Last 30 days)</option>
              <option value="inactive">Inactive</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          <div>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600"
                checked={advancedFilters.hasOrders}
                onChange={(e) =>
                  setAdvancedFilters({
                    ...advancedFilters,
                    hasOrders: e.target.checked,
                  })
                }
              />
              <span className="ml-2 text-gray-700">Has Orders</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end mt-4 space-x-2">
          <button
            type="button"
            onClick={() =>
              setAdvancedFilters({
                dateFrom: "",
                dateTo: "",
                hasOrders: false,
                status: "all",
              })
            }
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Reset
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </form>
    </div>
  );

  const renderTabContent = () => {
    if (detailsLoading) return <div>Loading client details...</div>;
    if (!clientDetails)
      return (
        <ClientDashboardPlaceholder
          setClients={setClients}
          setFilteredClients={setFilteredClients}
          toggleAdvancedSearch={() =>
            setShowAdvancedSearch(!showAdvancedSearch)
          }
          showAdvancedSearch={showAdvancedSearch}
          setShowAdvancedSearch={setShowAdvancedSearch}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          handleApplyFilters={handleApplyFilters}
        />
      );

    const { client, orders, contacts, invoices, notes, files } = clientDetails;

    // Add a button to close client details view on mobile
    const CloseButton = () => (
      <button
        onClick={() => {
          setSelectedClient(null);
          setClientDetails(null);
          setShowClientList(true);
        }}
        className="lg:hidden px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center mb-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mr-1"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to dashboard
      </button>
    );

    switch (activeTab) {
      case "info":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Client Information</h2>
            <p>
              <strong>Name:</strong> {client.firstName} {client.lastName}
            </p>
            <p>
              <strong>Email:</strong> {client.email}
            </p>
            <p>
              <strong>Phone:</strong> {client.phone}
            </p>
            <p>
              <strong>Company:</strong> {client.company}
            </p>
            {client.addresses && client.addresses.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold mt-4">Addresses</h3>
                {client.addresses.map((addr, index) => (
                  <div key={index} className="border p-2 my-2 rounded">
                    <p>
                      {addr.street}, {addr.city}, {addr.state},{" "}
                      {addr.postalCode}, {addr.country}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-gray-500">No address data available.</p>
            )}
            <div className="mt-4 flex flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-4">
              <button
                onClick={() => handleToggleAdminClick(client)}
                className={`px-4 py-2 font-normal ${
                  client.isAdmin
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-green-500 hover:bg-green-600"
                } text-white rounded`}
              >
                {client.isAdmin ? "Revoke Admin" : "Make Admin"}
              </button>
              <button
                onClick={() => handleResetPassword(client)}
                className="px-4 py-2 bg-blue-500 font-normal text-white rounded hover:bg-blue-600"
              >
                Send Reset Password Request
              </button>
              <button
                onClick={() => handleDeleteUser(client)}
                className="px-4 py-2 bg-red-600 text-white font-normal rounded hover:bg-red-700"
              >
                {/* <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg> */}
                Delete Client Account
              </button>
            </div>
            <div className="mt-4 text-sm text-gray-600 bg-gray-100 p-3 rounded">
              <p>
                <strong>Note:</strong> Deleting a user account will remove all
                their personal data and anonymize their orders. This action
                cannot be undone.
              </p>
            </div>
          </div>
        );

      // Add CloseButton to the other tabs as well
      case "contacts":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Contacts</h2>
            {contacts.length > 0 ? (
              contacts.map((contact, idx) => (
                <div key={idx} className="border p-2 my-2 rounded">
                  <p>
                    {contact.name} - {contact.email}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No contacts data available.</p>
            )}
          </div>
        );
      case "invoices":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Invoices</h2>
            {invoices.length > 0 ? (
              invoices.map((invoice, idx) => (
                <div key={idx} className="border p-2 my-2 rounded">
                  <p>
                    <strong>Invoice ID:</strong> {invoice._id}
                  </p>
                  <p>
                    <strong>Amount:</strong> ${invoice.amount}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No invoices data available.</p>
            )}
          </div>
        );
      case "orders":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Orders</h2>
            {orders.length > 0 ? (
              orders.map((order, idx) => (
                <div key={idx} className="border p-4 my-4 rounded flex">
                  {/* Order Image */}
                  <div className="w-24 h-24 flex-shrink-0 mr-4">
                    {order.products && order.products[0] ? (
                      <img
                        src={
                          order.products[0].customization?.preview
                            ? order.products[0].customization.preview.startsWith(
                                "/upload/"
                              )
                              ? `/api${order.products[0].customization.preview}`
                              : order.products[0].customization.preview
                            : order.products[0].product?.images?.[0]?.path
                            ? `/api${order.products[0].product.images[0].path}`
                            : order.products[0].product?.images?.[0]?.data
                            ? `data:${order.products[0].product.images[0].contentType};base64,${order.products[0].product.images[0].data}`
                            : "/api/upload/products/default-product.png"
                        }
                        alt={order.products[0].product?.name || "Order item"}
                        className="w-full h-full object-contain border rounded bg-gray-50"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 rounded border flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Order Details */}
                  <div className="flex-1">
                    <p>
                      <strong>Order ID:</strong> #{order._id.slice(-6)}
                    </p>
                    <p>
                      <strong>Total Amount:</strong> $
                      {order.totalAmount.toFixed(2)}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span className="capitalize">{order.status}</span>
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {new Date(order.createdAt).toLocaleDateString()}
                    </p>
                    {order.products && order.products[0] && (
                      <p>
                        <strong>Product:</strong>{" "}
                        {order.products[0].product?.name}
                      </p>
                    )}
                    {order.products && order.products.length > 1 && (
                      <p className="text-sm text-gray-600">
                        + {order.products.length - 1} more item(s)
                      </p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No orders data available.</p>
            )}
          </div>
        );
      case "notes":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Notes</h2>
            <div className="mb-4">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="w-full border p-2 rounded"
              />
              <button
                onClick={handleAddNote}
                className="mt-2 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
              >
                Add Note
              </button>
            </div>
            {notes.length > 0 ? (
              notes.map((note, idx) => (
                <div key={idx} className="border p-2 my-2 rounded">
                  <p>{note.note}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(note.createdAt).toLocaleString()}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No notes data available.</p>
            )}
          </div>
        );
      case "files":
        return (
          <div>
            <CloseButton />
            <h2 className="text-xl font-semibold mb-4">Files</h2>
            {clientDetails.files.length > 0 ? (
              clientDetails.files.map((file, idx) => (
                <div
                  key={idx}
                  className="border p-2 my-2 rounded flex items-center space-x-4"
                >
                  <div className="w-20 sm:w-32 h-20 sm:h-32 flex-shrink-0">
                    <img
                      src={
                        file.url
                          ? file.url.startsWith("/upload/")
                            ? `/api${file.url}`
                            : file.url.startsWith("http")
                            ? file.url
                            : `/api/upload/orders/${file.url}`
                          : "/api/upload/orders/default-image.png"
                      }
                      alt={`Order ${file.orderId} - Field ${file.fieldId}`}
                      className="w-full h-full object-contain border rounded bg-white"
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.target.src = "/api/upload/orders/default-image.png";
                        e.target.onerror = null; // Prevent infinite loop
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Order: #{file.orderId?.slice(-6) || file.orderId}
                    </p>
                    <p className="text-sm text-gray-600">
                      Field: {file.fieldId}
                    </p>
                    <p className="text-xs text-gray-500">
                      Product: {file.productId?.slice(-6) || "N/A"}
                    </p>
                    <button
                      onClick={() =>
                        handleDownloadImage(
                          file.url,
                          `order-${
                            file.orderId?.slice(-6) || file.orderId
                          }-field-${file.fieldId}.png`
                        )
                      }
                      className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No files data available.</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center">
        {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /> */}
        <img src="../images/loading.gif" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Client Management</h2>

      <div className="flex flex-col bg-white shadow rounded-2xl p-4 relative">
        {adminModal.show && adminModal.client && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() =>
                  setAdminModal({
                    show: false,
                    client: null,
                    isGranting: false,
                  })
                }
              ></div>

              <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div
                      className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                        adminModal.isGranting ? "bg-blue-100" : "bg-red-100"
                      } sm:mx-0 sm:h-10 sm:w-10`}
                    >
                      {adminModal.isGranting ? (
                        <User className="h-6 w-6 text-blue-600" />
                      ) : (
                        <User className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {adminModal.isGranting
                          ? "Grant Admin Rights"
                          : "Revoke Admin Rights"}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {adminModal.isGranting
                            ? `Are you sure you want to make ${adminModal.client.firstName} ${adminModal.client.lastName} an admin? They will have access to all administrative functions.`
                            : `Are you sure you want to revoke admin rights from ${adminModal.client.firstName} ${adminModal.client.lastName}? They will lose access to administrative functions.`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className={`w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 ${
                      adminModal.isGranting
                        ? "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                        : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                    } text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm`}
                    onClick={confirmToggleAdmin}
                  >
                    {adminModal.isGranting ? "Grant Access" : "Revoke Access"}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() =>
                      setAdminModal({
                        show: false,
                        client: null,
                        isGranting: false,
                      })
                    }
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Toggle Button - Only show when in client list view */}
        {(!selectedClient || showClientList) && (
          <button
            className="lg:hidden absolute top-4 right-4 z-10 p-2 bg-blue-500 text-white rounded-full"
            onClick={() => setShowClientList(!showClientList)}
          >
            <Menu size={20} />
          </button>
        )}

        <div className={`flex flex-col lg:flex-row`}>
          {/* Client List - Hidden on mobile when viewing details */}
          <div
            className={`w-full lg:w-1/3 lg:border-r lg:pr-4 ${
              selectedClient && !showClientList ? "hidden lg:block" : "block"
            }`}
          >
            <div className="flex items-center border rounded px-2 mb-4">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border-0 focus:outline-none"
              />
            </div>
            {loading ? (
              <div>Loading...</div>
            ) : (
              <div>
                <ul className="space-y-2">
                  {currentUsers.map((client) => (
                    <li
                      key={client._id}
                      style={{
                        borderBottom: "1px solid #eee", // Light thin border
                      }}
                    >
                      <button
                        style={{
                          boxShadow: "none",
                          transform: "none",
                        }}
                        onClick={() => {
                          setSelectedClient(client);
                          setActiveTab("info");
                          setShowClientList(false);
                        }}
                        className={`w-full text-left font-normal px-3 py-2 rounded ${
                          selectedClient && selectedClient._id === client._id
                            ? "bg-blue-500 text-white"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {client.firstName} {client.lastName}
                      </button>
                    </li>
                  ))}
                </ul>
                <Pagination />
              </div>
            )}
          </div>

          {/* Client Details - Full screen on mobile */}
          <div
            className={`w-full lg:w-2/3 lg:pl-4 mt-4 lg:mt-0 ${
              (selectedClient && !showClientList) ||
              !selectedClient ||
              showClientList === true
                ? "block"
                : "hidden"
            }`}
          >
            {selectedClient ? (
              <>
                {/* Mobile Back Button - Only show when viewing details */}
                {!showClientList && (
                  <div className="lg:hidden flex items-center mb-4">
                    <button
                      onClick={() => setShowClientList(true)}
                      className="flex items-center text-blue-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-1"
                      >
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                      </svg>
                      Back to clients
                    </button>
                    <h3 className="ml-2 font-medium truncate">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </h3>
                  </div>
                )}

                {/* Mobile Tab Navigation - Icons */}
                <div className="border-b mb-4 lg:hidden">
                  <nav className="flex justify-between px-2">
                    <button
                      onClick={() => setActiveTab("info")}
                      className={`p-3 ${
                        activeTab === "info"
                          ? "text-blue-600 border-b-2 border-blue-500"
                          : "text-gray-600"
                      }`}
                    >
                      <User size={20} />
                    </button>
                    <button
                      onClick={() => setActiveTab("invoices")}
                      className={`p-3 ${
                        activeTab === "invoices"
                          ? "text-blue-600 border-b-2 border-blue-500"
                          : "text-gray-600"
                      }`}
                    >
                      <FileText size={20} />
                    </button>
                    <button
                      onClick={() => setActiveTab("orders")}
                      className={`p-3 ${
                        activeTab === "orders"
                          ? "text-blue-600 border-b-2 border-blue-500"
                          : "text-gray-600"
                      }`}
                    >
                      <ShoppingCart size={20} />
                    </button>
                    <button
                      onClick={() => setActiveTab("notes")}
                      className={`p-3 ${
                        activeTab === "notes"
                          ? "text-blue-600 border-b-2 border-blue-500"
                          : "text-gray-600"
                      }`}
                    >
                      <Clipboard size={20} />
                    </button>
                    <button
                      onClick={() => setActiveTab("files")}
                      className={`p-3 ${
                        activeTab === "files"
                          ? "text-blue-600 border-b-2 border-blue-500"
                          : "text-gray-600"
                      }`}
                    >
                      <Image size={20} />
                    </button>
                  </nav>
                </div>

                {/* Desktop Tab Navigation - Text */}
                <div className="border-b mb-4 hidden lg:block">
                  <nav className="flex space-x-2 overflow-x-auto whitespace-nowrap">
                    {["info", "invoices", "orders", "notes", "files"].map(
                      (tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 py-2 font-medium ${
                            activeTab === tab
                              ? "border-b-2 border-blue-500 text-blue-600"
                              : "text-gray-600 hover:text-blue-600"
                          }`}
                        >
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>
                      )
                    )}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-2 lg:p-4 max-h-[65vh] overflow-y-auto">
                  {renderTabContent()}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <ClientDashboardPlaceholder
                  setClients={setClients}
                  setFilteredClients={setFilteredClients}
                  toggleAdvancedSearch={() =>
                    setShowAdvancedSearch(!showAdvancedSearch)
                  }
                  showAdvancedSearch={showAdvancedSearch}
                  setShowAdvancedSearch={setShowAdvancedSearch}
                  advancedFilters={advancedFilters}
                  setAdvancedFilters={setAdvancedFilters}
                  handleApplyFilters={handleApplyFilters}
                />
                {showAdvancedSearch && renderAdvancedSearch()}
              </div>
            )}
          </div>
          {notification && (
            <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded">
              {notification}
            </div>
          )}
        </div>
      </div>

      {deleteModal.show && deleteModal.client && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteModal({ show: false, client: null })}
            ></div>

            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete User Account
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete{" "}
                        {deleteModal.client.firstName}{" "}
                        {deleteModal.client.lastName}'s account? This action
                        cannot be undone.
                      </p>
                      <div className="mt-2 text-sm text-gray-500">
                        <p className="font-semibold mb-1">This will:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>Permanently remove the user's account</li>
                          <li>Anonymize any completed orders</li>
                          <li>Delete their cart data</li>
                        </ul>
                        <p className="mt-2 italic">
                          Note: Users with pending orders cannot be deleted.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={confirlgeleteUser}
                >
                  Delete Account
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setDeleteModal({ show: false, client: null })}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteSuccess && (
        <div className="fixed z-50 inset-0 flex items-center justify-center">
          <div className="bg-green-100 bg-opacity-90 rounded-full p-6 animate-pulse shadow-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManagement;
